import {
  BillableItem as BillableItemApi,
  BillableItemCustomerAddressInterface,
  Frequency as FrequencyApi,
  SubscriptionStatus as SubscriptionStatusApi,
  RenewalState as RenewalStateApi,
  Consumer,
  DisplayOption,
} from '@vendasta/billing';
import {
  Frequency,
  frequencyFromApi,
  ProductPricing,
  productPricingFromApi,
  SubscriptionStatus,
  subscriptionStatusFromApi,
} from '../pricing';
import { SubscriptionSource, subscriptionSourceFromApi } from '../pricing/source';

export interface ListBillableItemsFilter {
  skus?: string[];
  merchantId?: string;
  customerId?: string;
}

export interface ListSubscriptionsFilter {
  skus?: string[];
  merchantId?: string;
  customerId?: string;
  searchTerm?: string;
  status?: SubscriptionStatusApi;
  expiredDateGte?: Date;
  expiredDateLte?: Date;
  frequencies?: FrequencyApi[];
  retailSubscriptionGroupIds?: string[];
}

export interface SubscriptionsProjectionFilter {
  price?: boolean;
  includeRetailSubscriptionGroups?: boolean;
}

export interface ListSubscriptionsFilterHttp {
  skus?: string[];
  merchantId?: string;
  customerId?: string;
}

export interface CustomerAddress {
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

export interface BillableItem {
  subscriptionId: string;
  merchantId: string;
  sku: string;
  productName: string;
  customerId: string;
  customerName: string;
  orderId: string;
  expiry?: Date;
  billingStart?: Date;
  created?: Date;
  updated?: Date;
  frequency: Frequency;
  retailFrequency: Frequency;
  nextRenewal?: Date;
  nextRetailRenewal?: Date;
  customerAddress: CustomerAddress;
  customUnitPrice: number;
  retailUnitPrice: number;
  status?: SubscriptionStatus;
  price?: ProductPricing;
  source?: SubscriptionSource;
  autoBillable: boolean;
  description?: string;
  billingCycleAnchor?: Date;
  nextInvoiceDate?: Date;
  consumer: Consumer;
  retailSubscriptionGroupId?: string;
  retailSubscriptionGroupName?: string;
  retailSubscriptionGroupDisplayOption?: DisplayOption;
}

export enum RenewalState {
  Immediate = 'IMMEDIATE',
  Delayed = 'DELAYED',
}

function customerAddressFromApi(apiAddress: BillableItemCustomerAddressInterface | undefined): CustomerAddress {
  return {
    address: apiAddress?.address || '',
    city: apiAddress?.city || '',
    state: apiAddress?.state || '',
    zip: apiAddress?.zip || '',
    country: apiAddress?.country || '',
  };
}

export function billableItemFromApi(apiItem: BillableItemApi): BillableItem {
  const zeroDate = new Date('0001-01-01T00:00:00Z').valueOf();
  return {
    subscriptionId: apiItem.subscriptionId || '',
    merchantId: apiItem.merchantId || '',
    sku: apiItem.sku || '',
    productName: apiItem.productName || '',
    customerId: apiItem.customerId || '',
    customerName: apiItem.customerName || '',
    orderId: apiItem.orderId || '',
    expiry: apiItem.expiry && apiItem.expiry.valueOf() !== zeroDate ? new Date(apiItem.expiry) : undefined,
    billingStart: apiItem.billingStart && apiItem.billingStart.valueOf() !== zeroDate ? new Date(apiItem.billingStart) : undefined,
    created: apiItem.created && apiItem.created.valueOf() !== zeroDate ? new Date(apiItem.created) : undefined,
    updated: apiItem.updated && apiItem.updated.valueOf() !== zeroDate ? new Date(apiItem.updated) : undefined,
    frequency: frequencyFromApi(apiItem.frequency),
    retailFrequency: frequencyFromApi(apiItem.retailFrequency),
    nextRenewal: apiItem.nextRenewal && apiItem.nextRenewal.valueOf() !== zeroDate ? new Date(apiItem.nextRenewal) : undefined,
    nextRetailRenewal:
      apiItem.nextRetailRenewal && apiItem.nextRetailRenewal.valueOf() !== zeroDate ? new Date(apiItem.nextRetailRenewal) : undefined,
    customerAddress: customerAddressFromApi(apiItem.customerAddress),
    customUnitPrice: apiItem.customUnitPrice || 0,
    retailUnitPrice: apiItem.retailUnitPrice || 0,
    status: subscriptionStatusFromApi(apiItem.status),
    price: apiItem.price ? productPricingFromApi(apiItem.price) : undefined,
    source: subscriptionSourceFromApi(apiItem.source),
    autoBillable: apiItem.autoBillable,
    description: apiItem.description || '',
    billingCycleAnchor:
      apiItem.billingCycleAnchor?.valueOf() !== zeroDate ? new Date(apiItem.billingCycleAnchor) : undefined,
    nextInvoiceDate:
      apiItem.nextInvoiceDate && apiItem.nextInvoiceDate.valueOf() !== zeroDate
        ? new Date(apiItem.nextInvoiceDate)
        : undefined,
    consumer: apiItem.consumer,
    retailSubscriptionGroupId: apiItem.retailSubscriptionGroup?.id || '',
    retailSubscriptionGroupName: apiItem.retailSubscriptionGroup?.name || '',
    retailSubscriptionGroupDisplayOption: apiItem.retailSubscriptionGroup?.displayOption,
  };
}

export function listSubscriptionsFiltersToHTTPJson(filters: ListBillableItemsFilter): ListSubscriptionsFilterHttp {
  return {
    merchantId: filters.merchantId,
    customerId: filters.customerId,
    skus: filters.skus,
  };
}

export function renewalStateToApi(state: RenewalState): RenewalStateApi {
  if (state === RenewalState.Delayed) {
    return RenewalStateApi.RENEWAL_STATE_DELAYED;
  }
  return RenewalStateApi.RENEWAL_STATE_IMMEDIATE;
}
